import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { getCreatedToastMessage, getErrorToastMessage } from '@/libs/api'
import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type {
  Invoice,
  InvoiceCreditPayload,
  InvoiceListParams,
  InvoicePayload,
  InvoicePaymentPayload,
  InvoiceReminderPayload,
  InvoiceStatusPayload,
} from '@/libs/interfaces'
import { invoicesApi } from '@/services/api'

interface HookParams<T> {
  params: T
  enabled?: boolean
}

export function useInvoices({ params, enabled }: HookParams<InvoiceListParams>) {
  return useQuery<Invoice[]>({
    queryKey: QUERY_KEYS.invoices.list(params),
    queryFn: () => invoicesApi.list(params),
    enabled: (enabled ?? true) && !!params.orgID && !!params.storeID,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useInvoiceByID({
  params,
  enabled,
}: HookParams<{ orgID: string; storeID: string; billID: string }>) {
  return useQuery<Invoice>({
    queryKey: QUERY_KEYS.invoices.id(params.billID),
    queryFn: () => invoicesApi.getByID(params),
    enabled: (enabled ?? true) && !!params.orgID && !!params.storeID && !!params.billID,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useInfiniteInvoices({ params, enabled }: HookParams<InvoiceListParams>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.invoices.infinite(params),
    queryFn: ({ pageParam }) =>
      invoicesApi.listCursor({
        ...params,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.pageInfo.next || undefined,
    getPreviousPageParam: (firstPage) => firstPage.pageInfo.prev || undefined,
    enabled: (enabled ?? true) && !!params.orgID && !!params.storeID,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useCreateInvoice() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({
      orgID,
      storeID,
      payload,
    }: {
      orgID: string
      storeID: string
      payload: InvoicePayload
      // The create-bill response never carries a `message` field, so the
      // draft-vs-final distinction can't come from the backend — let callers
      // that know which one just happened pass it through instead.
      successMessage?: string
    }) => invoicesApi.create({ orgID, storeID }, payload),
    onSuccess: (result, variables) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all })
      toast.success(
        variables.successMessage ?? getCreatedToastMessage(result, t('billing.invoiceCreated')),
      )
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useUpdateInvoice() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({
      orgID,
      storeID,
      billID,
      payload,
    }: {
      orgID: string
      storeID: string
      billID: string
      payload: Partial<InvoicePayload>
    }) => invoicesApi.update({ orgID, storeID, billID }, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useDeleteInvoice() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: (params: { orgID: string; storeID: string; billID: string }) =>
      invoicesApi.delete(params),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

/** Imperatively loads a full bill (with `billRows`) — list rows are a slim shape, so
 *  anything that needs the line items (e.g. the PDF) must go through this. Served from
 *  the same cache entry as `useInvoiceByID`. */
export function useFetchInvoice() {
  const queryClient = useQueryClient()
  return useCallback(
    (params: { orgID: string; storeID: string; billID: string }) =>
      queryClient.fetchQuery({
        queryKey: QUERY_KEYS.invoices.id(params.billID),
        queryFn: () => invoicesApi.getByID(params),
        staleTime: STALE_TIME['1Hour'],
        // Callers await this from a click handler and surface failures themselves.
        meta: { skipErrorRedirect: true },
      }),
    [queryClient],
  )
}

export function useUpdateInvoiceStatus() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({
      storeID,
      billID,
      payload,
    }: {
      storeID: string
      billID: string
      payload: InvoiceStatusPayload
    }) => invoicesApi.updateStatus({ storeID, billID }, payload),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all })
      toast.success(result.message ?? t('billing.statusUpdated'))
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useSendInvoiceReminder() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: (params: { storeID: string; billID: string }) => invoicesApi.sendReminder(params),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all })
      toast.success(result.message ?? t('billing.reminderSent'))
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

type BillParams = { orgID: string; storeID: string; billID: string }

export function useRegisterInvoicePayment() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({ payload, ...params }: BillParams & { payload: InvoicePaymentPayload }) =>
      invoicesApi.registerPayment(params, payload),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all })
      toast.success(result.message ?? t('billing.paymentRegistered'))
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useCreateCreditInvoice() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({ payload, ...params }: BillParams & { payload: InvoiceCreditPayload }) =>
      invoicesApi.createCredit(params, payload),
    onSuccess: (creditInvoice) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all })
      toast.success(
        t('billing.creditInvoiceCreated', {
          number: t('billing.invoiceNumberValue', { number: creditInvoice.billNumber }),
        }),
      )
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useCreateReminderInvoice() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({ payload, ...params }: BillParams & { payload: InvoiceReminderPayload }) =>
      invoicesApi.createReminderInvoice(params, payload),
    onSuccess: (reminderInvoice) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all })
      toast.success(
        t('billing.reminderInvoiceCreated', {
          number: t('billing.invoiceNumberValue', { number: reminderInvoice.billNumber }),
        }),
      )
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useSendInvoiceEmail() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: (params: BillParams) => invoicesApi.sendEmail(params),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all })
      toast.success(result.message ?? t('billing.invoiceEmailed'))
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export type { Invoice, InvoicePayload }
